import { GATHER, GATHER_GROUND, GATHER_KINDS, GOLEMWORKS_PER_LEVEL, LAND_LEVELS, type GatherKind } from '@somnhold/content';
import { cellId } from '../grid.ts';
import { hexDist, hexLen, type Hex } from '../hex.ts';
import { idiv, imax } from '../math/fixed.ts';
import { sortedCellKeys } from './core.ts';
import { cellsOf } from './footprint.ts';
import type { MapDef } from '../types.ts';
import type { CampaignState } from './types.ts';

/*
 * Golems that gather on terraced land (docs/CONSTRUCCION.md §5). They are the group's: the table says how many
 * gather each resource, the rest build and repair, and those with nothing to build gather wood and stone. A
 * gatherer works the wood, rock or vein nearest a store (the keep, or a gathering post) and brings in less the
 * longer the trip.
 */

/** One resource unit of gathering: golems × per day × bp × ms. */
const UNIT = 10000 * 86400000;
const DAY = 24 * 3600000;

/** The golems the table sends gathering, by resource (none on the ring's bastion). */
export const gatherTable = (s: CampaignState): Record<GatherKind, number> => ({
  wood: s.golems?.wood ?? 0,
  stone: s.golems?.stone ?? 0,
  crystal: s.golems?.crystal ?? 0,
  iron: s.golems?.iron ?? 0,
});

/** The cells of the standing foundry (era II): where the golems bring the iron they dig. */
export const foundryCells = (s: CampaignState): Hex[] => {
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (b?.structure === 'foundry' && b.hp > 0) return cellsOf(s, key);
  }
  return [];
};

/** The golems the golem works adds (its level, while it stands). */
export const worksGolems = (s: CampaignState): number => {
  if (!s.map.land) return 0;
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (b?.structure === 'golemworks' && b.hp > 0) return b.level * GOLEMWORKS_PER_LEVEL;
  }
  return 0;
};

/** A place gathered loads are dropped at, and how many hexes its level takes off every trip from it. */
export type Store = Hex & { reach?: number };

/** Where gathered loads are dropped: the keep and every gathering post standing (each of its cells). */
export const stores = (s: CampaignState): Store[] => {
  const out: Store[] = [{ q: 0, r: 0 }];
  for (const key of sortedCellKeys(s)) {
    const b = s.cells[key]?.built;
    if (b?.structure !== 'depot' || b.hp <= 0) continue;
    // A gathering post raised higher reaches farther (LAND_LEVELS.depot).
    const reach = LAND_LEVELS.depot.reach[b.level - 1] ?? 0;
    for (const h of cellsOf(s, key)) out.push(reach > 0 ? { ...h, reach } : h);
  }
  return out;
};

/** What a golem brings in from a source `d` hexes from the nearest store, in bp of a full load. */
export const tripBp = (d: number): number => imax(GATHER.minBp, 10000 - imax(0, d - GATHER.nearHexes) * GATHER.perHexBp);

const SOURCES = new WeakMap<MapDef, Record<GatherKind, Hex[]>>();
/** Every cell of the map a golem can gather each resource from. */
const sourcesOf = (map: MapDef): Record<GatherKind, Hex[]> => {
  let out = SOURCES.get(map);
  if (out) return out;
  out = { wood: [], stone: [], crystal: [], iron: [] };
  const land = map.land;
  const R = map.radius;
  if (land) {
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        if (hexLen(q, r) > R) continue;
        const k = land.kind[cellId(R, q, r)] as number;
        for (const g of GATHER_KINDS) if (GATHER_GROUND[g] === k) out[g].push({ q, r });
      }
  }
  SOURCES.set(map, out);
  return out;
};

/** The best source of a resource for the stores standing now: where, and its trip in bp (null: none on the map). */
export const bestSource = (s: CampaignState, kind: GatherKind, all: Store[] = stores(s)): { at: Hex; bp: number; d: number } | null => {
  // Iron is smelted where it's dug: only the foundry takes it, and there is none without one.
  const at: Store[] = kind === 'iron' ? foundryCells(s) : all;
  if (at.length === 0) return null;
  let best: { at: Hex; bp: number; d: number } | null = null;
  for (const h of sourcesOf(s.map)[kind]) {
    let d = 0x7fffffff;
    for (const st of at) {
      const x = hexDist(h, st) - (st.reach ?? 0);
      if (x < d) d = x;
    }
    // Wood is walked into; rock and veins are worked from beside them.
    if (kind !== 'wood') d -= 1;
    if (!best || d < best.d) best = { at: h, bp: tripBp(d), d };
  }
  return best;
};

/** How many golems gather each resource right now, with `building` of them at work on jobs. */
export const gatherers = (s: CampaignState, total: number, building: number): Record<GatherKind, number> => {
  const out = gatherTable(s);
  let assigned = out.wood + out.stone + out.crystal + out.iron;
  // More in the table than there are golems: the last ones listed stay home.
  for (const g of ['iron', 'crystal', 'stone', 'wood'] as const) {
    while (assigned > total && out[g] > 0) {
      out[g]--;
      assigned--;
    }
  }
  // Golems with nothing to build gather wood and stone, half each (the odd one wood): never what the store
  // holds, so the sums don't hang on how often time is advanced.
  const idle = total - assigned - building;
  if (idle > 0) {
    out.wood += idle - idiv(idle, 2);
    out.stone += idiv(idle, 2);
  }
  return out;
};

/** Golems the table leaves for jobs. */
export const buildersFor = (s: CampaignState, total: number): number => {
  const t = gatherTable(s);
  return imax(0, total - t.wood - t.stone - t.crystal - t.iron);
};

/** What `n` golems on each resource bring in per day (whole units), for the panel. */
export const gatherPerDay = (s: CampaignState, n: Record<GatherKind, number>): Record<GatherKind, number> => {
  const at = stores(s);
  const out = { wood: 0, stone: 0, crystal: 0, iron: 0 };
  for (const g of GATHER_KINDS) {
    const src = n[g] > 0 ? bestSource(s, g, at) : null;
    if (src) out[g] = idiv(n[g] * GATHER.perDay[g] * src.bp, 10000);
  }
  return out;
};

/** Gathering for `dt` ms with `n` golems on each resource: whole units go to the store, the rest is carried over. */
export const accrue = (s: CampaignState, n: Record<GatherKind, number>, dt: number): void => {
  if (!s.map.land || dt <= 0) return;
  // A long stretch in day-long pieces, so the sums stay well inside exact integers.
  if (dt > DAY) {
    for (let left = dt; left > 0; left -= DAY) accrue(s, n, left > DAY ? DAY : left);
    return;
  }
  const at = stores(s);
  const acc = (s.gathered ??= { wood: 0, stone: 0, crystal: 0 });
  for (const g of GATHER_KINDS) {
    if (n[g] <= 0) continue;
    const src = bestSource(s, g, at);
    if (!src) continue;
    const had = acc[g] ?? 0;
    const sum = had + n[g] * GATHER.perDay[g] * src.bp * dt;
    const whole = idiv(sum, UNIT);
    acc[g] = sum - whole * UNIT;
    if (whole > 0) s.storage[g] = (s.storage[g] ?? 0) + whole;
  }
};
